'use client';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useThrottle } from './useThrottle';

export interface Position {
  top: number;
  bottom: number;
  scrollY: number;
  scrollX: number;
  height: number;
  width: number;
}

export interface UseElementPositionOptions {
  throttleMs?: number;
  useResizeObserver?: boolean;
  mutationObserverOptions?: MutationObserverInit;
}

const DEFAULT_MUTATION_OBSERVER_OPTIONS: MutationObserverInit = {
  attributeFilter: ['style', 'class'],
  attributes: true,
  childList: false,
  subtree: false,
};

const EMPTY_POSITION: Position = {
  bottom: 0,
  height: 0,
  scrollX: 0,
  scrollY: 0,
  top: 0,
  width: 0,
};

function isSamePosition(previous: Position, next: Position): boolean {
  return (
    previous.top === next.top &&
    previous.bottom === next.bottom &&
    previous.height === next.height &&
    previous.width === next.width &&
    previous.scrollX === next.scrollX &&
    previous.scrollY === next.scrollY
  );
}

export const useElementPosition = (elementId: string, options: UseElementPositionOptions = {}): Position => {
  const mutationObserverOptions = options.mutationObserverOptions ?? DEFAULT_MUTATION_OBSERVER_OPTIONS;
  const throttleMs = options.throttleMs ?? 100;
  const useResizeObserver = options.useResizeObserver ?? true;

  const [position, setPosition] = useState<Position>(EMPTY_POSITION);

  const rafIdRef = useRef<number | null>(null);
  const pendingUpdateRef = useRef(false);
  const mutationObserverOptionsRef = useRef(mutationObserverOptions);
  mutationObserverOptionsRef.current = mutationObserverOptions;

  const updatePosition = useCallback(() => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
    }

    rafIdRef.current = requestAnimationFrame(() => {
      const element = document.getElementById(elementId);
      if (element) {
        const rect = element.getBoundingClientRect();
        const next: Position = {
          bottom: rect.bottom,
          height: rect.height,
          scrollX: window.scrollX,
          scrollY: window.scrollY,
          top: rect.top,
          width: rect.width,
        };
        setPosition(previous => (isSamePosition(previous, next) ? previous : next));
      }
      pendingUpdateRef.current = false;
      rafIdRef.current = null;
    });
  }, [elementId]);

  const [throttledScrollUpdate] = useThrottle(updatePosition, throttleMs);
  const [throttledResizeUpdate] = useThrottle(updatePosition, throttleMs * 2);

  useEffect(() => {
    let cancelled = false;
    let observedElement: HTMLElement | null = null;
    let mutationObserver: MutationObserver | undefined;
    let resizeObserver: ResizeObserver | undefined;
    let documentObserver: MutationObserver | undefined;
    let zeroHeightRetryId: ReturnType<typeof setTimeout> | undefined;

    const scheduleUpdate = () => {
      if (!pendingUpdateRef.current) {
        pendingUpdateRef.current = true;
        updatePosition();
      }
    };

    const teardownElementListeners = () => {
      window.removeEventListener('scroll', throttledScrollUpdate);
      window.removeEventListener('resize', throttledResizeUpdate);
      window.removeEventListener('orientationchange', updatePosition);
      mutationObserver?.disconnect();
      mutationObserver = undefined;
      resizeObserver?.disconnect();
      resizeObserver = undefined;
      observedElement = null;
      if (zeroHeightRetryId !== undefined) {
        clearTimeout(zeroHeightRetryId);
        zeroHeightRetryId = undefined;
      }
    };

    const measureAndRetryIfEmpty = () => {
      updatePosition();
      if (zeroHeightRetryId !== undefined) {
        clearTimeout(zeroHeightRetryId);
      }

      let attempts = 0;
      const retry = () => {
        if (cancelled || attempts >= 8) return;
        attempts += 1;
        zeroHeightRetryId = setTimeout(() => {
          if (cancelled) return;
          const current = document.getElementById(elementId);
          if (!current) return;
          if (current.getBoundingClientRect().height > 0) {
            scheduleUpdate();
            return;
          }
          retry();
        }, 150 * attempts);
      };
      retry();
    };

    const observeElement = (element: HTMLElement) => {
      if (observedElement === element) {
        return;
      }

      teardownElementListeners();
      observedElement = element;

      window.addEventListener('scroll', throttledScrollUpdate, { passive: true });
      window.addEventListener('resize', throttledResizeUpdate);
      window.addEventListener('orientationchange', updatePosition);

      mutationObserver = new MutationObserver(scheduleUpdate);
      mutationObserver.observe(element, mutationObserverOptionsRef.current);

      if (useResizeObserver && typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(scheduleUpdate);
        resizeObserver.observe(element);
      }

      measureAndRetryIfEmpty();
    };

    const syncObservedElement = () => {
      if (cancelled) return;
      const current = document.getElementById(elementId);
      if (current === observedElement) return;
      if (current) {
        observeElement(current);
      } else {
        teardownElementListeners();
      }
    };

    syncObservedElement();

    if (typeof MutationObserver !== 'undefined') {
      documentObserver = new MutationObserver(syncObservedElement);
      documentObserver.observe(document.documentElement, { childList: true, subtree: true });
    }

    return () => {
      cancelled = true;
      pendingUpdateRef.current = false;
      teardownElementListeners();
      documentObserver?.disconnect();
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [elementId, updatePosition, throttledScrollUpdate, throttledResizeUpdate, useResizeObserver]);

  return position;
};

export default useElementPosition;
